import React, {useEffect, useRef, useState} from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components'
import { getAuth, GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth'
import {useDebounce} from "../hook/useDebounce";
import {useDispatch, useSelector} from "react-redux";
import {removeUser, setUser} from "../store/userSlice";

const Nav = () => {

    const initialUserData = localStorage.getItem('userData') ?
        JSON.parse(localStorage.getItem('userData')) : {};

    const [show, setShow] = useState(false);
    const { pathname } = useLocation();
    // const [searchValue, setSearchValue] = useState("");
    const navigate = useNavigate();
    const auth = getAuth();
    const provider = new GoogleAuthProvider();

    const inputRef = useRef(null);

    const { search } = useLocation();

    const query = new URLSearchParams(search).get("q") || "";
    const [searchValue, setSearchValue] = useState(query);

    const debouncedSearchValue = useDebounce(searchValue, 500);

    const dispatch = useDispatch();

    const userData = useSelector(state => state.user);

    // URL → state 동기화
    useEffect(() => {
        setSearchValue(query);
    }, [query]);

    // 디바운스된 값이 바뀌면 navigate
    useEffect(() => {
        // debouncedSearchValue가 공백이 아니거나, 처음 입력한 글자가 있을 때 실행
        if (debouncedSearchValue !== query) {
            navigate(`/search?q=${encodeURIComponent(debouncedSearchValue)}`, { replace: true });
        }
    }, [debouncedSearchValue]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
            inputRef.current.focus();
        }
    }, [pathname]); // 경로 바뀔 때마다 확인

    useEffect(() => {
        // pathname이 /main일 때 포커스 해제
        if (pathname === '/main' && inputRef.current) {
            inputRef.current.blur();
        }
    }, [pathname]);

    useEffect(() => {
        onAuthStateChanged(auth, (user) => {
            if (user) {
                if (pathname === "/") {
                    navigate("/main");
                }
            } else {
                navigate("/");
            }
        })
    }, [auth, navigate, pathname])

    useEffect(() => {
        window.addEventListener('scroll', handleScroll)
        return () => {
            window.removeEventListener('scroll', handleScroll);
        }
    }, [])

    // console.log('useLocation.search', useLocation().search);

    const handleScroll = () => {
        if (window.scrollY > 50) {
            setShow(true);
        } else {
            setShow(false);
        }
    }

    const handleChange = (e) => {
        setSearchValue(e.target.value);
        navigate(`/search?q=${e.target.value}`);
    }

    const handleClick = () => {
        navigate(`/search?q=${encodeURIComponent(searchValue || "")}`);
    };

    const handleAuth = () => {
        signInWithPopup(auth, provider)
            .then(result => {
                dispatch(setUser({
                    id: result.user.uid,
                    email: result.user.email,
                    displayName: result.user.displayName,
                    photoURL: result.user.photoURL
                }))
            })
            .catch(error => {
                console.log(error);
            })
    }

    const handleSignOut = () => {
        signOut(auth).then(() =>{
            dispatch(removeUser());
        }).catch((error) => {
            alert(error.message);
        })
    }

    return (
        <NavWrapper show={show}>
            <Logo>
                <img
                    alt="Disney Plus Logo"
                    src="/images/logo.svg"
                    onClick={() => (window.location.href = "/")}
                />
            </Logo>

            {pathname === "/" ?
                (<Login onClick={handleAuth}>Login</Login>) :
                <>
                    <Input
                        ref={inputRef}
                        value={searchValue}
                        onChange={handleChange}
                        onClick={handleClick}
                        className='nav__input'
                        type="text"
                        placeholder='검색해주세요.'
                    />

                    <SignOut>
                        <UserImg src={userData.photoURL} alt={userData.displayName} />
                        <DropDown>
                            <span onClick={handleSignOut}>Sign Out</span>
                        </DropDown>
                    </SignOut>
                </>
            }
        </NavWrapper>
    )
}

export default Nav

const DropDown = styled.div`
    position: absolute;
    top: 48px;
    right: 0px;
    background: rgb(19, 19, 19);
    border: 1px solid rgba(151, 151, 151, 0.34);
    border-radius:  4px;
    box-shadow: rgb(0 0 0 /50%) 0px 0px 18px 0px;
    padding: 10px;
    font-size: 14px;
    letter-spacing: 3px;
    width: 100%;
    opacity: 0;
`;

const SignOut = styled.div`
    position: relative;
    height: 48px;
    width: 48px;
    display: flex;
    cursor: pointer;
    align-items: center;
    justify-content: center;

    &:hover {
        ${DropDown} {
            opacity: 1;
            transition-duration: 1s;
        }
    }
`;

const UserImg = styled.img`
    border-radius: 50%;
    width: 100%;
    height: 100%;
`;


const Login = styled.a`
    background-color: rgba(0,0,0,0.6);
    padding: 8px 16px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    border: 1px solid #f9f9f9;
    transition: all 0.2s ease 0s;

    &:hover {
        background-color: #f9f9f9;
        color: gray;
        border-color: transparent;
    }
`;

const Input = styled.input`
    position: fixed;
    left: 50%;
    transform: translate(-50%, 0);
    background-color: black;
    border-radius: 5px;
    color: white;
    padding: 5px;
    border: none;

    /* focus 시 placeholder 완전 숨기기 */
    &:focus::placeholder {
        background-color: black;
        color: black;  /* 완전 투명 */
        opacity: 1;             /* 불투명도 0 */
    }
`;

const NavWrapper = styled.nav`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 70px;
    background-color: ${props => props.show ? "#090b13" : "transparent"};
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 36px;
    letter-spacing: 16px;
    z-index: 3;
`;

const Logo = styled.a`
    padding:0;
    width: 80px;
    margin-top: 4px;
    max-height: 70px;
    font-size: 0;
    diplay: inline-block;

    img {
        display: block;
        width: 100%;
    }
`